import { Card, Col, Row, Skeleton, Typography, Result } from "antd";
import axios from "axios";
import { Event, SharedQuota, StudyProgramQuota } from "interfaces/events";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import colors from "constants/colors";
import StudyProgramQuotaStep from "./Steps/StudyProgramQuota";
import TotalSeatsStep from "./Steps/TotalSeats";

const EventSeats = () => {
  const { Title, Paragraph, Text } = Typography;
  const [event, setEvent] = useState<Event>();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState<boolean>(false);
  const [sharedQuotas, setSharedQuotas] = useState<SharedQuota[]>([]);
  const [studyProgramQuotas, setStudyProgramQuotas] = useState<StudyProgramQuota[]>([]);
  const [currentSharedQuota, setCurrentSharedQuota] = useState<SharedQuota | undefined>();
  const [registeredStudents, setRegisteredStudents] = useState<number>();

  useEffect(() => {
    setLoading(true);
    axios
      .get<Event>(`/events/all-events/${id}/`)
      .then(response => {
        setEvent(response.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    axios.get(`/events/${id}/registrations/`).then(response => {
      setRegisteredStudents(response.data.length);
    });
  }, [id]);

  useEffect(() => {
    axios.get<SharedQuota[]>(`/events/shared-quotas/`).then(response => {
      setSharedQuotas(response.data.filter(quota => quota.event === Number(id)));
    });
    axios.get<StudyProgramQuota[]>(`/events/${id}/study-program-quotas/`).then(response => {
      setStudyProgramQuotas(response.data);
    });
  }, [id]);

  const handleQuotas = (sq: SharedQuota[], spq: StudyProgramQuota[], csq: SharedQuota | undefined) => {
    setSharedQuotas(sq);
    setStudyProgramQuotas(spq);
    setCurrentSharedQuota(csq);
  };

  if (!event && !loading)
    return (
      <div style={{ display: "flex", justifyContent: "center", width: "100%" }}>
        <Result title="Fant ikke arrangementet" subTitle="Dobbeltsjekk URL eller prøv på nytt" status="404" />
      </div>
    );
  return (
    <>
      <Title>Fordel plasser</Title>
      {loading || !event ? (
        <Skeleton />
      ) : (
        <Row gutter={[20, 20]}>
          <Col lg={18} md={24} xs={24}>
            <StudyProgramQuotaStep
              sharedQuotas={sharedQuotas}
              studyProgramQuotas={studyProgramQuotas}
              currentSharedQuota={currentSharedQuota}
              handleQuotas={handleQuotas}
              eventId={Number(id)}
            />
            <TotalSeatsStep
              studyProgramQuotas={studyProgramQuotas}
              sharedQuotas={sharedQuotas}
              currentSharedQuota={currentSharedQuota}
              handleQuotas={handleQuotas}
            />
          </Col>
          <Col lg={6} md={24} xs={24}>
            <Card title="Totalt" style={{ marginBottom: "20px" }}>
              <Row>
                <Col span={12}>
                  <Title style={{ color: colors.primary, margin: "0" }}>
                    {sharedQuotas.reduce((total, quota) => total + quota.max_students_allowed, 0)}
                  </Title>
                  <Text style={{ color: colors.secondary }}>PLASSER</Text>
                </Col>
                <Col span={12}>
                  <Title style={{ color: colors.primary, margin: "0" }}>
                    {registeredStudents !== undefined ? registeredStudents : <Skeleton.Button active size="large" />}
                  </Title>
                  <Text style={{ color: colors.secondary }}>PÅMELDTE</Text>
                </Col>
              </Row>
            </Card>
            <Card title="Forklaringer">
              <Title level={5}>1. Linje og årskull</Title>
              <Paragraph>
                Velg en studielinje og de aktuelle årskullene, og angi maksimalt antall studenter for denne gruppen. For
                eksempel kan du tillate opptil 10 studenter fra førsteår Indøk.
              </Paragraph>
              <Paragraph>
                Hvis du ønsker å inkludere linjen i en eksisterende kvote, velg kvoten i steg 2, og trykk på{" "}
                <Text italic>Legg til i kvote</Text>.
              </Paragraph>
              <Paragraph>
                Hvis du ønsker å opprette en ny kvote for linjen, huk av for <Text italic>Ny kvote</Text> i steg 1, og
                trykk på <Text italic>Legg til i ny kvote</Text>.
              </Paragraph>
              <Title level={5}>2. Kvoter (Delte plasser)</Title>
              <Paragraph>
                Angi hvor mange plasser som kan deles mellom ulike studentgrupper i samme kvote. For eksempel kan du
                sette at førsteår Indøk og førsteår Data totalt kan dele på 10 plasser.
              </Paragraph>
              <Paragraph italic>
                (Merk: Hvis både førsteår Indøk og førsteår Data opprinnelig har 10 plasser hver, vil det totale
                antallet plasser i kvoten være avgjørende. Det vil si at hvis de ti første som melder seg på er fra
                førsteår Indøk, vil det ikke være noen plasser igjen for førsteår Data.)
              </Paragraph>
              <Paragraph italic>
                Det er summen av plasser totalt per kvote, ikke per linje, som avgjør hvor mange plasser det er på
                arrangementet.
              </Paragraph>
            </Card>
          </Col>
        </Row>
      )}
    </>
  );
};

export default EventSeats;
